import React, { useState } from 'react';
import { FlexAlignType, Pressable, StyleSheet, TextStyle } from 'react-native';
import theme from 'shared/styles/theme';

import BannerView from './BannerView';
import {
  AlignmentStyle,
  ComposableStyle,
  SystemBannerProps,
  SystemBannerType,
} from './SystemBanner.types';

export { SystemBannerType };

interface AlertState {
  default: ComposableStyle;
  focus: ComposableStyle;
  hover: ComposableStyle;
  press: ComposableStyle;
}

interface ComposableStyles {
  INFO: AlertState;
  SUCCESS: AlertState;
  WARNING: AlertState;
  ERROR: AlertState;
  BRAND: AlertState;
}

const composeAlignmentStyle = (
  onCardPress: SystemBannerProps['onCardPress'],
  onDismiss: SystemBannerProps['onDismiss'],
  button: SystemBannerProps['button'],
): AlignmentStyle => {
  let containerAlignment: FlexAlignType = 'flex-start';

  if (onDismiss) {
    containerAlignment = 'flex-start';
  } else if (onCardPress) {
    containerAlignment = 'center';
  }
  const innerContainerAlignment = button ? 'flex-start' : 'center';

  return {
    container: {
      alignItems: containerAlignment,
    },
    innerContainer: {
      alignItems: innerContainerAlignment,
    },
  };
};

const composeTextColorStyle = (
  bannerType: SystemBannerProps['bannerType'],
): TextStyle => ({
  color:
    bannerType === SystemBannerType.WARNING
      ? theme.color.text.primary
      : theme.color.interactive.inverse,
});

export const SystemBanner = ({
  iconKeyType,
  titleText,
  bodyText,
  bannerType,
  button,
  onDismiss,
  onCardPress,
}: SystemBannerProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  const computedStyles = composeSystemBannerStyles(
    bannerType,
    isHovered,
    isPressed,
    isFocused,
  );

  if (onDismiss && onCardPress) {
    console.warn(
      'Both onDismiss and onCardPress are passed. Only one should be used.',
    );
  }

  const alignmentStyles = composeAlignmentStyle(onCardPress, onDismiss, button);
  const textColorStyles = composeTextColorStyle(bannerType);

  const handleOnDismissAction = () => {
    setShowBanner(false);
    onDismiss?.();
  };

  if (!showBanner) {
    return null;
  }

  return onCardPress && !onDismiss && !button ? (
    <Pressable
      onPress={onCardPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
    >
      <BannerView
        iconKeyType={iconKeyType}
        titleText={titleText}
        bodyText={bodyText}
        bannerType={bannerType}
        button={button}
        onDismiss={onDismiss ? handleOnDismissAction : undefined}
        alignmentStyles={alignmentStyles}
        computedStyles={computedStyles}
        textColorStyles={textColorStyles}
        onCardPress={onCardPress}
      />
    </Pressable>
  ) : (
    <BannerView
      iconKeyType={iconKeyType}
      titleText={titleText}
      bodyText={bodyText}
      bannerType={bannerType}
      button={button}
      onDismiss={onDismiss ? handleOnDismissAction : undefined}
      alignmentStyles={alignmentStyles}
      computedStyles={computedStyles}
      textColorStyles={textColorStyles}
      onCardPress={onCardPress}
    />
  );
};

const systemBannerStyle: ComposableStyles = {
  INFO: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.info,
        borderColor: theme.color.status.info,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.info,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.infoHover,
        borderColor: theme.color.status.infoHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.infoPressed,
        borderColor: theme.color.status.infoPressed,
      },
    }),
  },
  SUCCESS: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positive,
        borderColor: theme.color.status.positive,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positive,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positiveHover,
        borderColor: theme.color.status.positiveHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positivePressed,
        borderColor: theme.color.status.positivePressed,
      },
    }),
  },
  WARNING: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warning,
        borderColor: theme.color.status.warning,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warning,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warningHover,
        borderColor: theme.color.status.warningHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warningPressed,
        borderColor: theme.color.status.warningPressed,
      },
    }),
  },
  ERROR: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negative,
        borderColor: theme.color.status.negative,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negative,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negativeHover,
        borderColor: theme.color.status.negativeHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negativePressed,
        borderColor: theme.color.status.negativePressed,
      },
    }),
  },
  BRAND: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.interactive.primary,
        borderColor: theme.color.interactive.primary,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.interactive.primary,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.interactive.primaryHovered,
        borderColor: theme.color.interactive.primaryHovered,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.interactive.primaryPressed,
        borderColor: theme.color.interactive.primaryPressed,
      },
    }),
  },
};

const composeSystemBannerStyles = (
  type: SystemBannerProps['bannerType'],
  isHovered: boolean,
  isPressed: boolean,
  isFocused: boolean,
): ComposableStyle => {
  const getState = () => {
    if (isFocused) {
      return 'focus';
    } else if (isHovered) {
      return 'hover';
    } else if (isPressed) {
      return 'press';
    }

    return 'default';
  };

  return { container: systemBannerStyle[type][getState()].container };
};
